import { EnumCompetenciaMode } from '@components/ComboCompetenciaMode';
import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { TableLinearLoading } from '@components/Table/TableLinearLoading';
import { TableWrapper } from '@components/Table/TableWrapper';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useFormatter from '@hooks/useFormatter';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { EnumSortDirection } from '@hooks/useRequest';
import { useValidation } from '@hooks/useValidation';
import { useValueState } from '@hooks/useValueState';
import { Box, Card, CardContent, CardHeader, CircularProgress, Typography } from '@material-ui/core';
import { EnumEmpresa, useEmpresaCombo } from '@model/Empresa';
import { useRemetenteWhatsappCombo } from '@model/RemetenteWhatsapp';
import { IInadimplenciaFaturaFilter, IInadimplenciaFaturaRecord, IInadimplenciaFaturaSummary, useRelatorioService } from '@services/RelatorioService';
import { yup } from '@validations/Yup';
import React from 'react';
import { InadimplenciaFaturaFilter } from './InadimplenciaFaturaFilter';
import { InadimplenciaFaturaTable } from './InadimplenciaFaturaTable';

export type RelatorioInadimplenciaFaturaProps = {

}

export function RelatorioInadimplenciaFatura (props: RelatorioInadimplenciaFaturaProps) {
    const summaryData = useValueState<IInadimplenciaFaturaSummary>();

    const data = useData<IInadimplenciaFaturaRecord, IInadimplenciaFaturaFilter>({
        filter: {
            empresaId: useField(EnumEmpresa.None),
            competencia: useField(),
            modoCompetencia: useField(EnumCompetenciaMode.CurrentOnly),
            whatsappEnvio: useField()
        },

        sortField: {
            name: "faturaId",
            direction: EnumSortDirection.Desc
        }
    });
    
    const loading = useLoading();
    const message = useMessage();
    const loadingSummary = useLoading();
    const formatter = useFormatter();
    const empresaCombo = useEmpresaCombo();
    const remetenteWhatsappCombo = useRemetenteWhatsappCombo();

    const relatorioService = useRelatorioService();

    const validation = useValidation<IInadimplenciaFaturaFilter>({
        modoCompetencia: yup.number().when(["competencia"], {
            is: (competencia: any) => (competencia || "").toString() !== "",
            then: yup.number().required()
        }),
    })

    const handleCsvButtonClick = React.useCallback(() => {
        if (!loading.isLoading) {
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loading.start();
                    relatorioService.exportarInadimplenciaFatura(data.requestedData)
                    .catch(message.showBlobException)
                    .finally(loading.stop);
                })
                .catch(data.filter.setErrorsFromYup);
            }
    }, [data.filter.setErrorsFromYup, data.requestedData, loading, message.showBlobException, relatorioService, validation]);

    React.useEffect(() => {
        if (!loading.isLoading) {
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loading.start();
                    relatorioService.listarInadimplenciaFatura(data.requestedData)
                        .then(data.handleResultResponse)
                        .catch(message.showException)
                        .finally(loading.stop);
                })
                .catch(data.filter.setErrorsFromYup);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    React.useEffect(() => {
        if (!loadingSummary.isLoading) {
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loadingSummary.start();
                    relatorioService.obterInadimplenciaFaturaSumario(data.filter.requestedFilter)
                        .then(summaryData.handleResponse)
                        .catch(message.showException)
                        .finally(loadingSummary.stop);
                })
                .catch(data.filter.setErrorsFromYup);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.filter.requestedFilter]);

    useEffectOnce(() => {
        empresaCombo.loadOptions();
        remetenteWhatsappCombo.loadOptions();
    })

    return (
        <React.Fragment>
            <Main>
                <PageTitle>
                    <PageTitleText text="Relatório de Inadimplência - Por Fatura" />
                </PageTitle>

                <PageContent>
                    <InadimplenciaFaturaFilter
                        filter={data.filter}
                        isLoading={loading.isLoading}
                        empresaCombo={empresaCombo}
                        remetenteWhatsappCombo={remetenteWhatsappCombo}
                    />

                    <Box style={{marginTop: 24, marginBottom: 24}}>
                        <HorizontalGrid spacing={2}>
                            <GridItem xs={4}>
                                <Card elevation={15}>
                                    <CardHeader title="Total de Faturas" />
                                    <CardContent>
                                        {
                                            loading.isLoading
                                            ? <Box style={{textAlign: 'center'}}><CircularProgress /></Box>
                                            : <Typography variant="h3">{summaryData.value?.count || ""}</Typography>
                                        }
                                    </CardContent>
                                </Card>
                            </GridItem>
                            
                            <GridItem xs={4}>
                                <Card elevation={15}>
                                    <CardHeader title="Saldo Total" />
                                    <CardContent>
                                        {
                                            loading.isLoading
                                            ? <Box style={{textAlign: 'center'}}><CircularProgress /></Box>
                                            : <Typography variant="h3">{formatter.toFloat(summaryData.value?.sumSaldo) || ""}</Typography>
                                        }
                                    </CardContent>
                                </Card>
                            </GridItem>

                            <GridItem xs={4}>
                                <Card elevation={15}>
                                    <CardHeader title="Vlr. Pago Total" />
                                    <CardContent>
                                        {
                                            loading.isLoading
                                            ? <Box style={{textAlign: 'center'}}><CircularProgress /></Box>
                                            : <Typography variant="h3">{formatter.toFloat(summaryData.value?.sumValorPago) || ""}</Typography>
                                        }
                                    </CardContent>
                                </Card>
                            </GridItem>
                        </HorizontalGrid>
                    </Box>

                    <TableWrapper>
                        <TableLinearLoading isLoading={loading.isLoading} />
                        <InadimplenciaFaturaTable
                            data={data}
                            isLoading={loading.isLoading}
                            onCsvButtonClick={handleCsvButtonClick}
                        />
                    </TableWrapper>
                </PageContent>
            </Main>
        </React.Fragment>
    )
}